<template>
  <v-container fluid class="dynamic-lists-container">
    <!-- Header mit Aktionsbuttons -->
    <v-row class="mb-4">
      <v-col>
        <h1 class="text-h4 font-weight-bold">
          <v-icon large color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
          Meine Listen
        </h1>
        <p class="text-subtitle-1 text-grey">
          Erstellen und verwalten Sie dynamische Listen und Formulare
        </p>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="success"
          size="large"
          prepend-icon="mdi-plus-circle"
          @click="openCreateListDialog"
        >
          Neue Liste erstellen
        </v-btn>
        <v-btn
          color="info"
          size="large"
          prepend-icon="mdi-history"
          class="ml-2"
          @click="showAuditLog = true"
        >
          Audit-Log
        </v-btn>
      </v-col>
    </v-row>

    <!-- Listen-Übersicht -->
    <v-row v-if="!loading && lists.length > 0">
      <v-col
        v-for="list in lists"
        :key="list.id"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          class="list-card"
          :style="{ borderTop: `4px solid ${list.color}` }"
          hover
          @click="openList(list.id)"
        >
          <v-card-title>
            <v-icon :color="list.color" class="mr-2">{{ list.icon }}</v-icon>
            {{ list.name }}
          </v-card-title>
          <v-card-subtitle>
            {{ list.description || 'Keine Beschreibung' }}
          </v-card-subtitle>
          <v-card-text>
            <v-chip size="small" class="mr-2">
              <v-icon start>mdi-table-column</v-icon>
              {{ list.column_count }} Spalten
            </v-chip>
            <v-chip size="small">
              <v-icon start>mdi-database</v-icon>
              {{ list.data_count }} Einträge
            </v-chip>
            <div class="mt-2 text-caption text-grey">
              Erstellt von {{ list.created_by_name }}
            </div>
            <div v-if="list.permissions && list.permissions.length > 0" class="mt-1 text-caption text-grey">
              <v-icon size="x-small" class="mr-1">mdi-account-multiple</v-icon>
              Zugriff: {{ getPermissionsSummary(list.permissions) }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              icon
              size="small"
              @click.stop="editList(list)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              @click.stop="deleteList(list)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="primary"
              @click.stop="openList(list.id)"
            >
              Öffnen
              <v-icon end>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-row v-if="!loading && lists.length === 0">
      <v-col class="text-center py-12">
        <v-icon size="120" color="grey-lighten-1">mdi-format-list-bulleted-square</v-icon>
        <h2 class="text-h5 mt-4 mb-2">Noch keine Listen</h2>
        <p class="text-grey mb-6">Erstellen Sie Ihre erste dynamische Liste</p>
        <v-btn
          color="primary"
          size="large"
          prepend-icon="mdi-plus"
          @click="openCreateListDialog"
        >
          Erste Liste erstellen
        </v-btn>
      </v-col>
    </v-row>

    <!-- Loading -->
    <v-row v-if="loading">
      <v-col class="text-center py-12">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-col>
    </v-row>

    <!-- Dialog: Liste erstellen/bearbeiten -->
    <v-dialog v-model="listDialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="bg-primary">
          <v-icon class="mr-2">{{ editingList ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ editingList ? 'Liste bearbeiten' : 'Neue Liste erstellen' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-form ref="listForm">
            <!-- Basis-Informationen -->
            <v-text-field
              v-model="currentList.name"
              label="Listen-Name"
              :rules="[v => !!v || 'Name erforderlich']"
              required
              prepend-icon="mdi-format-title"
            ></v-text-field>

            <v-textarea
              v-model="currentList.description"
              label="Beschreibung"
              rows="2"
              prepend-icon="mdi-text"
            ></v-textarea>

            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="currentList.icon"
                  label="Icon (Material Design Icon)"
                  prepend-icon="mdi-emoticon"
                  hint="z.B. mdi-clipboard-list"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="currentList.color"
                  label="Farbe"
                  type="color"
                  prepend-icon="mdi-palette"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Berechtigungen -->
            <v-expansion-panels class="my-4">
              <v-expansion-panel>
                <v-expansion-panel-title>
                  <v-icon class="mr-2">mdi-shield-account</v-icon>
                  Berechtigungen & Einstellungen
                </v-expansion-panel-title>
                <v-expansion-panel-text>

                  <v-divider class="my-3"></v-divider>

                  <h4 class="text-subtitle-1 mb-3">Benutzer-Berechtigungen</h4>

                  <v-list density="compact">
                    <v-list-item
                      v-for="(perm, index) in currentList.permissions"
                      :key="index"
                      class="permission-item mb-3 pa-3"
                    >
                      <v-row dense align="center">
                        <v-col cols="12" md="5">
                          <v-row dense>
                            <v-col cols="6">
                              <v-select
                                v-model="perm.permission_type"
                                :items="permissionTypes"
                                label="Typ"
                                density="compact"
                                hide-details
                              ></v-select>
                            </v-col>
                            <v-col cols="6">
                              <v-select
                                v-if="perm.permission_type === 'user'"
                                v-model="perm.entity_id"
                                :items="availableUsers"
                                item-title="name"
                                item-value="id"
                                label="Benutzer"
                                density="compact"
                                hide-details
                                clearable
                              ></v-select>
                              <v-select
                                v-else-if="perm.permission_type === 'group'"
                                v-model="perm.entity_id"
                                :items="availableGroups"
                                item-title="name"
                                item-value="id"
                                label="Bereich/Gruppe"
                                density="compact"
                                hide-details
                                clearable
                              ></v-select>
                              <v-select
                                v-else-if="perm.permission_type === 'role'"
                                v-model="perm.entity_id"
                                :items="availableRoles"
                                item-title="name"
                                item-value="id"
                                label="Rolle"
                                density="compact"
                                hide-details
                                clearable
                              ></v-select>
                              <v-chip v-else size="small" color="primary">Öffentlich</v-chip>
                            </v-col>
                          </v-row>
                        </v-col>
                        <v-col cols="12" md="6">
                          <div class="d-flex flex-wrap align-center" style="gap: 12px;">
                            <v-checkbox v-model="perm.can_view" label="Ansehen" density="compact" hide-details></v-checkbox>
                            <v-checkbox v-model="perm.can_create" label="Erstellen" density="compact" hide-details></v-checkbox>
                            <v-checkbox v-model="perm.can_edit" label="Bearbeiten" density="compact" hide-details></v-checkbox>
                            <v-checkbox v-model="perm.can_delete" label="Löschen" density="compact" hide-details></v-checkbox>
                            <v-checkbox v-model="perm.can_export" label="Export" density="compact" hide-details></v-checkbox>
                          </div>
                        </v-col>
                        <v-col cols="12" md="1" class="d-flex align-center justify-end">
                          <v-btn
                            icon="mdi-delete"
                            size="small"
                            color="error"
                            variant="text"
                            @click="removePermission(index)"
                          ></v-btn>
                        </v-col>
                      </v-row>
                    </v-list-item>
                  </v-list>

                  <v-btn
                    block
                    color="primary"
                    variant="outlined"
                    prepend-icon="mdi-plus"
                    @click="addPermission"
                    class="mt-2"
                  >
                    Berechtigung hinzufügen
                  </v-btn>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-divider class="my-4"></v-divider>

            <!-- Spalten-Definition -->
            <h3 class="text-h6 mb-3">
              <v-icon class="mr-2">mdi-table-column</v-icon>
              Spalten definieren
            </h3>

            <v-list>
              <v-list-item
                v-for="(column, index) in currentList.columns"
                :key="index"
                class="column-item mb-2"
              >
                <template v-slot:prepend>
                  <v-icon>mdi-drag-vertical</v-icon>
                </template>

                <v-row dense>
                  <v-col cols="3">
                    <v-text-field
                      v-model="column.column_name"
                      label="Spaltenname"
                      density="compact"
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-select
                      v-model="column.column_type"
                      :items="columnTypes"
                      label="Typ"
                      density="compact"
                      hide-details
                    ></v-select>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model="column.help_text"
                      label="Hilfetext"
                      density="compact"
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2" class="d-flex align-center">
                    <v-checkbox
                      v-model="column.is_required"
                      label="Pflicht"
                      density="compact"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                </v-row>

                <!-- Options für Select/Multiselect -->
                <v-row v-if="['select', 'multiselect'].includes(column.column_type)" dense class="mt-2">
                  <v-col>
                    <v-combobox
                      v-model="column.options"
                      label="Optionen (Enter für neue Option)"
                      multiple
                      chips
                      density="compact"
                      hide-details
                    ></v-combobox>
                  </v-col>
                </v-row>

                <template v-slot:append>
                  <v-btn
                    icon
                    size="small"
                    color="error"
                    @click="removeColumn(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>

            <v-btn
              block
              color="primary"
              variant="outlined"
              prepend-icon="mdi-plus"
              @click="addColumn"
              class="mt-2"
            >
              Spalte hinzufügen
            </v-btn>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="listDialog = false">Abbrechen</v-btn>
          <v-btn
            color="success"
            @click="saveList"
            :loading="saving"
          >
            {{ editingList ? 'Aktualisieren' : 'Erstellen' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Audit-Log -->
    <v-dialog v-model="showAuditLog" max-width="1200px" scrollable>
      <v-card>
        <v-card-title class="bg-info">
          <v-icon class="mr-2">mdi-history</v-icon>
          Audit-Log
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <AuditLogViewer />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showAuditLog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import AuditLogViewer from './AuditLogViewer.vue';

const router = useRouter();

// State
const lists = ref([]);
const loading = ref(true);
const listDialog = ref(false);
const showAuditLog = ref(false);
const editingList = ref(null);
const saving = ref(false);

const currentList = ref({
  name: '',
  description: '',
  icon: 'mdi-format-list-bulleted',
  color: '#1976D2',
  allow_import: true,
  allow_export: true,
  columns: [],
  permissions: []
});

const availableUsers = ref([]);
const availableGroups = ref([]);
const availableRoles = ref([]);

const permissionTypes = [
  { title: 'Benutzer', value: 'user' },
  { title: 'Bereich/Gruppe', value: 'group' },
  { title: 'Rolle', value: 'role' },
  { title: 'Öffentlich', value: 'public' }
];

const columnTypes = [
  { title: 'Text', value: 'text' },
  { title: 'Zahl', value: 'number' },
  { title: 'Datum', value: 'date' },
  { title: 'Datum & Uhrzeit', value: 'datetime' },
  { title: 'Auswahl', value: 'select' },
  { title: 'Mehrfachauswahl', value: 'multiselect' },
  { title: 'Checkbox', value: 'checkbox' },
  { title: 'E-Mail', value: 'email' },
  { title: 'Telefon', value: 'phone' },
  { title: 'URL', value: 'url' },
  { title: 'Textfeld (mehrzeilig)', value: 'textarea' },
  { title: 'Benutzer', value: 'user' },
  { title: 'Datei', value: 'file' }
];

// Methods
const loadLists = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}dynamic_lists.php?action=all`, {
      withCredentials: true
    });
    lists.value = response.data.lists;
  } catch (error) {
    console.error('Fehler beim Laden der Listen:', error);
  } finally {
    loading.value = false;
  }
};

const openCreateListDialog = () => {
  editingList.value = null;
  currentList.value = {
    name: '',
    description: '',
    icon: 'mdi-format-list-bulleted',
    color: '#1976D2',
    allow_import: true,
    allow_export: true,
    columns: [
      { column_name: 'Name', column_type: 'text', is_required: true, help_text: '' }
    ],
    permissions: []
  };
  listDialog.value = true;
};

const addPermission = () => {
  currentList.value.permissions.push({
    permission_type: 'user',
    entity_id: null,
    can_view: true,
    can_create: false,
    can_edit: false,
    can_delete: false
  });
};

const removePermission = (index) => {
  currentList.value.permissions.splice(index, 1);
};

const editList = async (list) => {
  editingList.value = list;

  // Liste mit Spalten vom Backend laden
  try {
    const response = await axios.get(`${API_BASE_URL}dynamic_lists.php?list_id=${list.id}`, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      // Konvertiere Berechtigungen von 1/0 zu true/false
      const permissions = (response.data.list_permissions || []).map(perm => ({
        ...perm,
        can_view: Boolean(perm.can_view),
        can_create: Boolean(perm.can_create),
        can_edit: Boolean(perm.can_edit),
        can_delete: Boolean(perm.can_delete),
        can_export: Boolean(perm.can_export),
        can_manage: Boolean(perm.can_manage)
      }));

      currentList.value = {
        ...response.data.list,
        columns: response.data.columns || [],
        permissions: permissions,
        allow_export: Boolean(response.data.list.allow_export),
        allow_import: Boolean(response.data.list.allow_import)
      };
    }
  } catch (error) {
    console.error('Fehler beim Laden der Liste:', error);
    currentList.value = {
      ...list,
      columns: [],
      permissions: [],
      allow_export: Boolean(list.allow_export),
      allow_import: Boolean(list.allow_import)
    };
  }

  listDialog.value = true;
};

const addColumn = () => {
  currentList.value.columns.push({
    column_name: '',
    column_type: 'text',
    is_required: false,
    help_text: '',
    options: []
  });
};

const removeColumn = (index) => {
  currentList.value.columns.splice(index, 1);
};

const saveList = async () => {
  saving.value = true;
  try {
    const action = editingList.value ? 'update_list' : 'create_list';

    const payload = {
      action,
      ...currentList.value
    };

    if (editingList.value) {
      payload.list_id = editingList.value.id;
    }

    const response = await axios.post(`${API_BASE_URL}dynamic_lists.php`, payload, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      listDialog.value = false;
      await loadLists();

      // Bei neuer Liste direkt öffnen
      if (!editingList.value && response.data.list_id) {
        openList(response.data.list_id);
      }
    }
  } catch (error) {
    console.error('Fehler beim Speichern:', error);
  } finally {
    saving.value = false;
  }
};

const deleteList = async (list) => {
  if (!confirm(`Liste "${list.name}" wirklich löschen?`)) return;

  try {
    await axios.delete(`${API_BASE_URL}dynamic_lists.php`, {
      data: { list_id: list.id },
      withCredentials: true
    });
    await loadLists();
  } catch (error) {
    console.error('Fehler beim Löschen:', error);
  }
};

const openList = (listId) => {
  router.push(`/lists/${listId}`);
};

const getPermissionsSummary = (permissions) => {
  if (!permissions || permissions.length === 0) return 'Nur Ersteller';

  const names = permissions.map(p => {
    if (p.permission_type === 'public') return 'Öffentlich';
    return p.display_name || p.entity_id;
  });

  if (names.length > 3) {
    return `${names.slice(0, 3).join(', ')} +${names.length - 3} weitere`;
  }

  return names.join(', ');
};

const loadUsers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}dynamic_lists.php?action=users`, {
      withCredentials: true
    });
    availableUsers.value = response.data.users.map(u => ({
      id: u.id,
      name: `${u.vorname} ${u.nachname} (${u.email})`,
      bereich: u.bereich
    }));
  } catch (error) {
    console.error('Fehler beim Laden der Benutzer:', error);
  }
};

const loadGroupsAndRoles = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}get_groups_roles.php?action=get`, {
      withCredentials: true
    });

    // Nur Bereiche (Abteilungen) verwenden
    const groups = [];

    if (response.data.bereiche && Array.isArray(response.data.bereiche)) {
      response.data.bereiche.forEach(bereich => {
        groups.push({
          id: bereich.id,
          name: bereich.name,
          type: bereich.type
        });
      });
    }

    availableGroups.value = groups;

    // Rollen
    if (response.data.verfuegbare_rollen) {
      availableRoles.value = response.data.verfuegbare_rollen
        .filter(r => r.count > 0)
        .map(r => ({
          id: r.id,
          name: `${r.name} (${r.count})`
        }));
    }
  } catch (error) {
    console.error('Fehler beim Laden der Gruppen/Rollen:', error);
  }
};

// Lifecycle
onMounted(() => {
  loadLists();
  loadUsers();
  loadGroupsAndRoles();
});
</script>

<style scoped>
.dynamic-lists-container {
  max-width: 1400px;
  margin: 0 auto;
}

.list-card {
  transition: all 0.3s ease;
  cursor: pointer;
}

.list-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.column-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px;
}

.column-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.permission-item {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}

.permission-item:hover {
  background: rgba(255, 255, 255, 0.05);
}
</style>
